import { CommonModule } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	forwardRef,
	Input,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { LucideAngularModule } from 'lucide-angular';

@Component({
	selector: 'app-input',
	imports: [CommonModule, LucideAngularModule, TranslocoModule],
	templateUrl: './input.component.html',
	styleUrl: './input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputComponent),
			multi: true,
		},
	],
})
export class InputComponent extends ControlValueAccessorBase<string> {
	@Input() placeholder = '';
	@Input() size: 'large' | 'medium' | 'small' = 'medium';
	@Input() disabled = false;
	@Input() loading = false;
	@Input() error = false;
	@Input() errorMessage = '';
	@Input() icon = '';
	@Input() iconPosition: 'left' | 'right' = 'left';
	@Input() label?: string;
	@Input() helperText?: string;
	@Input() inputId = `input-${Math.random().toString(36).substring(2, 9)}`;
	@Input() type: 'text' | 'email' | 'tel' | 'password' | 'number' = 'text';
	@Input() showToggle = true;
	/** Shows a red asterisk after the label and marks the control as aria-required. */
	@Input() required = false;

	@ContentChild('suffix') suffix: TemplateRef<any> | null = null;

	@ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;

	// Password visibility toggle
	showPassword = false;

	constructor(private cdRef: ChangeDetectorRef) {
		super();
	}

	/**
	 * Get current input type (handles password toggle)
	 */
	get currentInputType(): 'text' | 'email' | 'tel' | 'password' | 'number' {
		return this.type === 'password' && this.showPassword ? 'text' : this.type;
	}

	/**
	 * Check if password toggle button should be shown
	 */
	get showToggleButton(): boolean {
		return this.type === 'password' && this.showToggle;
	}

	/**
	 * Toggle password visibility
	 */
	togglePasswordVisibility(): void {
		this.showPassword = !this.showPassword;
	}

	/**
	 * Callback triggered on native input change.
	 * Updates the internal value and notifies Angular.
	 */
	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.value = input.value;
	}

	/**
	 * Allows Angular to set the disabled state of the input.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	/**
	 * Ensures the DOM input reflects the latest value written from Angular.
	 */
	override writeValue(value: string): void {
		super.writeValue(value);
		this.cdRef.markForCheck(); // forces re-evaluation if using OnPush
		if (this.inputRef) {
			this.inputRef.nativeElement.value = value ?? '';
		}
	}
}
